import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import MultiGoalAnalytics from "../components/MultiGoalAnalytics";
import { toast } from "sonner";
import { Loader2, TrendingUp, Flame, CheckCircle2, Target } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from "recharts";

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ readiness_score: 0, streak_count: 0, completion_rate: 0 });
  const [days, setDays] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([api.get("/progress"), api.get("/tasks/history")]);
      setProgress(p.data);
      setDays(h.data.days || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onSwitch = () => { setLoading(true); load(); };
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => window.removeEventListener("adyap:goal-switched", onSwitch);
  }, [load]);

  if (loading) return <AppLayout><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AppLayout>;

  // build cumulative completion trend
  let cumDone = 0, cumTotal = 0;
  const trend = days.map((d) => {
    cumDone += d.tasks.filter((t) => t.completed).length;
    cumTotal += d.tasks.length;
    return { day: `D${d.day_number}`, readiness: cumTotal ? Math.round((cumDone / cumTotal) * 100) : 0 };
  });

  const perDay = days.map((d) => ({
    day: `D${d.day_number}`,
    done: d.tasks.filter((t) => t.completed).length,
    total: d.tasks.length,
  }));

  const totalTasks = days.reduce((a, d) => a + d.tasks.length, 0);
  const completedTasks = days.reduce((a, d) => a + d.tasks.filter((t) => t.completed).length, 0);

  return (
    <AppLayout>
      <div className="mb-8">
        <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Progress Analytics</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-extrabold mt-1">Your readiness, measured.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: TrendingUp, label: "Readiness", value: `${progress.readiness_score}/100`, c: "text-primary" },
          { icon: Flame, label: "Streak", value: `${progress.streak_count} days`, c: "text-accent" },
          { icon: CheckCircle2, label: "Completion", value: `${progress.completion_rate}%`, c: "text-emerald-400" },
          { icon: Target, label: "Tasks done", value: `${completedTasks}/${totalTasks}`, c: "text-purple-400" },
        ].map((s) => (
          <div key={s.label} className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold"><s.icon className={`w-4 h-4 ${s.c}`} /> {s.label}</div>
            <div className="font-heading text-2xl font-extrabold mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <MultiGoalAnalytics />
      </div>

      <div className="glass rounded-2xl p-6 mb-6" data-testid="readiness-chart">
        <h2 className="font-heading text-lg font-semibold mb-1">Readiness trend</h2>
        <p className="text-sm text-zinc-400 mb-4">Cumulative completion drives your readiness score over the plan.</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
            <AreaChart data={trend.length ? trend : [{ day: "D1", readiness: 0 }]}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#52525b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#52525b" tick={{ fontSize: 11 }} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Area type="monotone" dataKey="readiness" stroke="#10B981" strokeWidth={2} fill="url(#grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="daily-chart">
        <h2 className="font-heading text-lg font-semibold mb-4">Tasks completed per day</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
            <BarChart data={perDay.length ? perDay : [{ day: "D1", done: 0, total: 0 }]}>
              <XAxis dataKey="day" stroke="#52525b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#52525b" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="done" radius={[4, 4, 0, 0]}>
                {perDay.map((e, i) => <Cell key={i} fill={e.total && e.done === e.total ? "#10B981" : "#38BDF8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AppLayout>
  );
}
